<template>
  <div>
    <table-component
      ref="userTable"
      :data="fetchData"
      sort-by="food_name"
      sort-order="asc"
      :show-caption="false"
      class="datatable"
      :show-filter="false">
      <table-column show="id" label="Id" :hidden="true"></table-column>
      <table-column
        show="food_name"
        :label="$t('health.nutritionalPlan.name')"
        header-class="text-left"
        cell-class="table-component__text-left">
      </table-column>
      <table-column
        show="amount"
        :label="$t('health.nutritionalPlan.amount')"
        header-class="text-left"
        cell-class="table-component__text-left">
      </table-column>

      <table-column
        show="carbs"
        :label="$t('health.nutritionalPlan.carbs')"
        header-class="text-left"
        cell-class="table-component__text-left">
      </table-column>

      <table-column
        show="protein"
        :label="$t('health.nutritionalPlan.protein')"
        header-class="text-left"
        cell-class="table-component__text-left">
      </table-column>

      <table-column
        show="fat"
        :label="$t('health.nutritionalPlan.fat')"
        header-class="text-left"
        cell-class="table-component__text-left">
      </table-column>

      <table-column
        show="calories"
        :label="$t('health.nutritionalPlan.calories')"
        header-class="text-left"
        cell-class="table-component__text-left">
      </table-column>

      <table-column
        v-if="showFibre"
        show="fibre"
        :label="$t('health.nutritionalPlan.fibre')"
        header-class="text-left"
        cell-class="table-component__text-left">
      </table-column>

      <table-column
        label=""
        :sortable="false"
        :filterable="false"
        header-class="table-component__minimum__width__column"
        cell-class="text-center">
        <template slot-scope="row">
          <div v-if="!noWeight">
            <font-awesome-icon
              v-if="row.recalculate"
              :id="row"
              v-on:click="onChangeCalculate(row)"
              :icon="icon.calculator"
              class="table-iconset-icon pointer"/>
            <font-awesome-icon
              v-else
              :id="row"
              v-on:click="onChangeCalculate(row)"
              :icon="icon.add"
              class="table-icon-disabled rotated pointer"/>
          </div>
        </template>
      </table-column>
      <table-column
        label=""
        :sortable="false"
        :filterable="false"
        header-class="table-component__minimum__width__column"
        cell-class="text-center">
        <template slot-scope="row">
          <font-awesome-icon
            :id="row"
            v-on:click="onUpdate(row)"
            :icon="icon.pencil"
            class="table-iconset-icon"/>
        </template>
      </table-column>

      <table-column
        label=""
        :sortable="false"
        :filterable="false"
        header-class="table-component__minimum__width__column"
        cell-class="text-center">
        <template slot-scope="row">
          <font-awesome-icon
            v-on:click="onDelete(row)"
            :icon="icon.trash"
            class="table-iconset-icon"/>
        </template>
      </table-column>
    </table-component>

    <input-text-modal
      :title="$t('health.nutritionalPlan.modifyAmount')"
      v-if="showInputTextModal"
      v-model="newAmount"
      :error="showInputTextModalError"
      :errorMessage="showInputTextModalErrorMessage"
      :okButton="$t('commons.save')"
      :label="$t('health.nutritionalPlan.amountInG')"
      v-on:cancel="cancelUpdateAmount"
      v-on:ok="updateAmount"/>
  </div>
</template>

<script>
import {faPencilAlt, faTrashAlt, faCalculator} from '@fortawesome/pro-regular-svg-icons';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableRow from '@/components/shared/table/components/TableRow';
import TableColumn from '@/components/shared/table/components/TableColumn';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import InputTextModal from '@/components/shared/dialogs/InputTextModal';
import pagination from '@/components/shared/table/pagination';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';

export default {
  name: 'FoodTable',
  components: {
    TableComponent,
    TableRow,
    TableColumn,
    FontAwesomeIcon,
    InputTextModal
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    noWeight: {
      type: Boolean,
      default: false
    },
    showFibre: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      icon: {
        trash: faTrashAlt,
        pencil: faPencilAlt,
        calculator: faCalculator,
        add: faPlusCircle
      },
      showInputTextModal: false,
      newAmount: undefined,
      showInputTextModalError: false,
      showInputTextModalErrorMessage: '',
      toUpdateFood: undefined,
      tablePageSize: 5,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      }
    };
  },
  watch: {
    getItems: {
      handler: function () {
        this.$refs.userTable.refresh();
      }
    }
  },
  computed: {
    getItems () {
      return this.items;
    }
  },
  methods: {
    onDelete (food) {
      this.$emit('delete', food);
    },
    onUpdate (food) {
      this.toUpdateFood = food;
      this.showInputTextModalError = false;
      this.showInputTextModal = true;
    },
    onChangeCalculate (food) {
      this.$emit('updateRecalculate', food);
    },
    updateAmount () {
      if (this.newAmount === undefined || this.newAmount === '' || isNaN(this.newAmount)) {
        this.showInputTextModalError = true;
        this.showInputTextModalErrorMessage = this.$t('health.nutritionalPlan.amountModifyError');
      } else {
        this.newAmount = parseFloat(this.newAmount);
        if (this.newAmount < 0) {
          this.showInputTextModalErrorMessage = this.$t('health.nutritionalPlan.negativeAmount');
          this.showInputTextModalError = true;
        } else {
          this.toUpdateFood.amount = this.newAmount;
          this.$emit('updateAmount', this.toUpdateFood);
          this.showInputTextModal = false;
        }
      }
      this.newAmount = '';
    },
    cancelUpdateAmount () {
      this.showInputTextModal = false;
      this.newAmount = undefined;
    },
    fetchData ({page, filter, sort}) {
      page = page === null || isNaN(page) ? 1 : page;
      const tablePage = pagination.createPage(
        this.getItems,
        this.tableState,
        ['food_name'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    }
  }
};
</script>

<style scoped>
.rotated {
  transform: rotate(45deg);
}

.pointer {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/components/nutritionPlan/FoodTable.vue